import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GalleryCarousel } from '@/components/GalleryCarousel';
import { ProgramImageTile } from '@/components/ProgramImageTile';
import { Section } from '@/components/Section';
import { events, track } from '@/lib/analytics';
import { PROGRAMS } from '@/lib/content';
import { HERO_CANDIDATES } from '@/lib/images';

// React 18.3 doesn't recognize the `fetchPriority` prop at runtime (lands in React 19).
// Set the lowercase HTML attribute via ref so the browser still sees the priority hint
// for the LCP hero image.
function setHighFetchPriority(el: HTMLImageElement | null) {
  if (el) el.setAttribute('fetchpriority', 'high');
}

export function HomePage() {
  useEffect(() => {
    track(events.homeView);
  }, []);

  const programs = PROGRAMS;

  // Default to candidate 5 (the class group photo) per owner preference.
  const hero = HERO_CANDIDATES[4];

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative h-[80vh] min-h-[560px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            ref={setHighFetchPriority}
            src={hero.src}
            alt="George Prevalsky's Boxing & Muay Thai Gym, class warmup"
            decoding="async"
            className="h-full w-full object-cover brightness-50"
            style={{ objectPosition: hero.objectPosition }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-ink/40 to-ink" />
        </div>
        <div className="container-x relative flex h-full flex-col justify-end pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-accent">
              Irving, Texas
            </div>
            <h1 className="heading-display max-w-4xl uppercase text-bone">
              George Prevalsky&apos;s Gym
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-bone/80">
              Boxing, Muay Thai, Brazilian, Jiujitsu and MMA.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/trial" className="btn-primary">
                Book a Trial Class
              </Link>
              <Link to="/programs" className="btn-ghost">
                Explore programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <GalleryCarousel />

      {/* ── How we teach ─────────────────────────────────────────────── */}
      <Section eyebrow="How we teach" title="30 years refining the process.">
        <div className="grid items-start gap-10 md:grid-cols-2">
          <div className="overflow-hidden rounded-lg border border-bone/10 bg-neutral-900/40">
            <div className="relative aspect-video w-full">
              <iframe
                title="George coaching a fighter"
                src="https://www.youtube-nocookie.com/embed/Ku4U9xk4uB8?autoplay=1&mute=1&loop=1&playlist=Ku4U9xk4uB8&controls=1&modestbranding=1&playsinline=1&rel=0&vq=hd1080"
                className="absolute inset-0 h-full w-full"
                loading="lazy"
                allow="autoplay; encrypted-media; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
          <div className="space-y-4 text-bone/70">
            <p>
              George&apos;s gym opened in 1996. We&apos;ve spent three decades refining one thing:
              how to actually teach the craft. Students advance through the curriculum in groups
              based on skill level. Beginners and championship-level athletes train under the same
              system, just at different stages of it.
            </p>
            <p>
              Most gyms throw everyone in together regardless of experience, or push you toward
              expensive private lessons. Our curriculum meets you wherever you start and builds you
              up from scratch, layering fundamentals until you have the technique to spar, compete,
              or simply move like a fighter should.
            </p>
            <p>
              In 2010 we relocated to our current facility: a standalone 6,000 sq.ft. building,
              designed and built from the ground up for high-quality Muay Thai and Boxing. The
              process has been battle-tested across every skill level, and it&apos;s what produces
              some of the best amateur and professional fighters in the Dallas area.
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-4 border-t border-bone/10 pt-6">
              <div>
                <dt className="text-xs uppercase tracking-wider text-bone/50">Established</dt>
                <dd className="mt-1 font-display text-2xl text-bone">1996</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-bone/50">Facility</dt>
                <dd className="mt-1 font-display text-2xl text-bone">6,000 sq.ft.</dd>
              </div>
            </dl>
          </div>
        </div>
      </Section>

      {/* ── Gym video ────────────────────────────────────────────────── */}
      <Section eyebrow="Inside the gym" title="See it in motion.">
        <div className="overflow-hidden rounded-lg border border-bone/10 bg-neutral-900/40">
          <div className="relative aspect-video w-full">
            <iframe
              title="George Prevalsky's Boxing & Muay Thai Gym"
              src="https://www.youtube-nocookie.com/embed/xWgicdTvxwM?autoplay=1&mute=1&loop=1&playlist=xWgicdTvxwM&controls=1&modestbranding=1&playsinline=1&rel=0"
              className="absolute inset-0 h-full w-full"
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </Section>

      {/* ── Programs ─────────────────────────────────────────────────── */}
      <Section eyebrow="Programs" title="Pick your discipline.">
        <p className="mb-10 max-w-3xl text-bone/70">
          We specialize in Muay Thai, Boxing, and MMA, with Brazilian Jiu-Jitsu and wrestling taught
          by some of the most experienced instructors in the area.
        </p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {programs.map((p) => {
            return (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="overflow-hidden rounded-lg border border-bone/10 bg-neutral-900/40 hover:border-accent/40"
              >
                <ProgramImageTile slug={p.slug} title={p.title} aspectClassName="aspect-[4/3]" />
                <div className="p-5">
                  <div className="font-display text-2xl tracking-wide">{p.title}</div>
                  <p className="mt-2 text-sm text-bone/70">{p.blurb}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
        <div className="mt-8">
          <Link to="/programs" className="text-accent hover:underline">
            See all programs →
          </Link>
        </div>
      </Section>

      {/* ── Location ─────────────────────────────────────────────────── */}
      <Section eyebrow="Location" title="Visit the gym.">
        <div className="grid items-stretch gap-8 md:grid-cols-2">
          <div className="flex flex-col">
            <p className="font-display text-2xl">
              George Prevalsky&apos;s Boxing &amp; Muay Thai Gym
            </p>
            <p className="mt-3 text-bone/80">
              612 Valley Ranch Pkwy S<br />
              Irving, TX 75063
            </p>
            <p className="mt-3 text-sm text-bone/60">
              Southeast corner of MacArthur Blvd &amp; Valley Ranch Pkwy South. Next to Discount
              Tire and behind CVS, about 2 blocks north of I-635. 6,000 sq.ft. standalone facility.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=612+Valley+Ranch+Pkwy+S,+Irving,+TX+75063"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Get directions →
              </a>
              <Link to="/location" className="btn-ghost">
                Hours &amp; details
              </Link>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden rounded-lg border border-bone/10 bg-neutral-900/40 md:aspect-auto md:min-h-[320px]">
            <iframe
              title="Map - 612 Valley Ranch Pkwy S, Irving, TX"
              src="https://www.google.com/maps?q=612+Valley+Ranch+Pkwy+S,+Irving,+TX+75063&output=embed"
              className="h-full min-h-[320px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </Section>

      {/* ── Contact ──────────────────────────────────────────────────── */}
      <Section eyebrow="Contact" title="Call the gym.">
        <div className="grid max-w-2xl gap-4">
          <p className="text-bone/70">
            Front desk for programs, schedule, drop-ins, and pricing. Tap to call.
          </p>
          <a
            href="tel:+19729775605"
            onClick={() => track(events.contactSubmit, { method: 'tel', source: 'home' })}
            className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-5 text-lg sm:w-fit"
            aria-label="Call the gym at (972) 977-5605"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Call (972) 977-5605
          </a>
          <p className="text-sm text-bone/60">612 Valley Ranch Pkwy S · Irving, TX 75063</p>
        </div>
      </Section>
    </>
  );
}
